import { QuestionTypeEnum } from './enums.js';
/**
 * Total over the enum on purpose — see the note above. Do not widen the key
 * type to `string`; the exhaustiveness is the point.
 */
export const QUESTION_TYPES = {
    [QuestionTypeEnum.SELECT_ONE]: {
        isText: false,
        isChartable: true,
        isMultiValue: false,
        hasOptions: true,
        emptyAnswer: () => '',
    },
    [QuestionTypeEnum.SELECT_MANY]: {
        isText: false,
        isChartable: true,
        isMultiValue: true,
        hasOptions: true,
        emptyAnswer: () => ({ subAnswers: [] }),
    },
    [QuestionTypeEnum.LONG_TEXT]: {
        isText: true,
        isChartable: true,
        isMultiValue: false,
        hasOptions: false,
        emptyAnswer: () => '',
    },
    [QuestionTypeEnum.SHORT_TEXT]: {
        isText: true,
        isChartable: true,
        isMultiValue: false,
        hasOptions: false,
        emptyAnswer: () => '',
    },
    [QuestionTypeEnum.DATE]: {
        // Not text: a date is not free prose, and charting it as verbatims or
        // word frequency would be nonsense.
        isText: false,
        isChartable: true,
        isMultiValue: false,
        hasOptions: false,
        emptyAnswer: () => '',
    },
    [QuestionTypeEnum.SCALE]: {
        isText: false,
        isChartable: true,
        isMultiValue: false,
        // No option list: the answer is the number, and the range lives on the
        // question's `scale`.
        hasOptions: false,
        // null, not '' — see the note on NUMBER.
        emptyAnswer: () => null,
    },
    [QuestionTypeEnum.NUMBER]: {
        isText: false,
        isChartable: true,
        isMultiValue: false,
        hasOptions: false,
        // Unanswered must not be an empty string: Number('') is 0, and a blank
        // exported as zero reads as a recorded value rather than a missing one.
        emptyAnswer: () => null,
    },
};
export const QUESTION_TYPE_VALUES = Object.keys(QUESTION_TYPES);
/**
 * The guard every entry point uses before trusting a type.
 *
 * Anything else — a model's invention, a hand-edited import, a type from a
 * newer version of the app — is unknown, and the callers treat an unknown type
 * as unrenderable and never required rather than pretending it is text.
 */
export const isKnownQuestionType = (type) => typeof type === 'string' &&
    Object.prototype.hasOwnProperty.call(QUESTION_TYPES, type);
const metaFor = (type) => isKnownQuestionType(type) ? QUESTION_TYPES[type] : undefined;
/** Unknown types answer `false` to all of these, which is the safe direction. */
export const isTextQuestion = (type) => !!metaFor(type)?.isText;
export const isChartableQuestion = (type) => !!metaFor(type)?.isChartable;
export const isMultiValueQuestion = (type) => !!metaFor(type)?.isMultiValue;
export const questionTypeHasOptions = (type) => !!metaFor(type)?.hasOptions;
/**
 * Stores a number as the answer itself.
 *
 * These need no scoring annotation: the value a respondent chose is already
 * its position on the scale, so an average or a top-box works the moment the
 * question is asked.
 */
export const isNumericQuestion = (type) => type === QuestionTypeEnum.SCALE || type === QuestionTypeEnum.NUMBER;
/**
 * An unknown type stores an empty string, matching what the renderer and the
 * validation schema do with it: nothing is asked, so nothing is recorded.
 */
export const emptyAnswerFor = (type) => {
    const meta = metaFor(type);
    // Not `?? ''`: a numeric type's empty answer IS null, and coalescing it to
    // an empty string would reintroduce the Number('') === 0 problem the null
    // exists to avoid.
    return meta ? meta.emptyAnswer() : '';
};
/**
 * Replaces unusable question types in a template that came from outside.
 *
 * The AI modal passes a model's JSON straight into `createFormTemplate`, and a
 * model asked for a survey will cheerfully emit `"type": "rating"` or
 * `"multiple_choice"`. Neither exists, and saving one produces a question that
 * renders as nothing.
 *
 * Coerced to short text rather than dropped: the model was asked something and
 * answered, so the question itself is usually fine and only its type is
 * invented. Free text is the one form that can hold any answer, so the
 * question survives and the author can change it. Dropping it would lose work
 * silently, which is the failure mode this whole change exists to remove.
 *
 * What it must not do is coerce quietly, which is why the caller is handed
 * every change to put in front of someone.
 */
export const coerceUnknownQuestionTypes = (template) => {
    const source = template;
    if (!source?.pages?.length)
        return { template, coerced: [] };
    const coerced = [];
    const pages = source.pages.map((page, pageIndex) => {
        if (!page?.questions?.length)
            return page;
        return {
            ...page,
            questions: page.questions.map((question, questionIndex) => {
                if (isKnownQuestionType(question?.type))
                    return question;
                coerced.push({
                    pageIndex,
                    questionIndex,
                    question: String(question?.question ?? ''),
                    from: String(question?.type ?? ''),
                });
                return { ...question, type: QuestionTypeEnum.SHORT_TEXT };
            }),
        };
    });
    return { template: { ...source, pages }, coerced };
};
